<template>
  <NeueLoading :loading="loading">
    <div v-if="data.list.length">
      <div class="flex items-center justify-end gap-2 mb-4">
        <Popover as="div" class="relative">
          <PopoverButton
            class="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <span class="truncate">{{ dropFilterLabel }}</span>
            <ChevronDownIcon class="h-4 w-4 text-gray-500 shrink-0" aria-hidden="true" />
          </PopoverButton>
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <PopoverPanel
              class="absolute right-0 z-20 mt-2 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 px-4 py-1"
            >
              <PopoverButton
                class="text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block"
                @click="setDropFilter('upcoming')"
              >
                Upcoming
              </PopoverButton>
              <PopoverButton
                class="text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block"
                @click="setDropFilter('past')"
              >
                Past
              </PopoverButton>
              <PopoverButton
                class="text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block"
                @click="setDropFilter('noDays')"
              >
                No production days
              </PopoverButton>
            </PopoverPanel>
          </transition>
        </Popover>
      </div>

      <div v-if="activeList.length">
        <div class="overflow-x-auto p-2 -m-2">
          <NeueTablist>
            <template #head>
              <TablistHeader>
                <TablistGrid :columns="dropColumns" header>
                  <div>Drop</div>
                  <div>Description</div>
                  <div>Customer Smart Lists</div>
                  <div>Store</div>
                  <div>Production</div>
                  <div>Autocharge</div>
                  <div></div>
                  <div>Status</div>
                </TablistGrid>
              </TablistHeader>
            </template>
            <TablistRow
              v-for="item in activeList.slice(data.page * 8, data.page * 8 + 8)"
              :key="item.id"
              class="cursor-pointer"
              @click="$router.push(linkBase + '/' + item.id)"
            >
              <TablistGrid :columns="dropColumns">
                <div class="text-gray-900 font-bold font-mono truncate">#{{ item.id }}</div>
                <div class="text-sm font-medium text-gray-900">
                  {{ item.attributes.description }}
                  <span v-if="duplicatorForDrop(item)">({{ recurrenceForDrop(item) }})</span>
                </div>
                <div class="text-sm text-gray-900">
                  {{
                    groupsForDrop(item)
                      .map(x => x?.attributes?.audience_name)
                      .join(', ')
                  }}
                </div>
                <div class="text-sm text-gray-900 truncate">
                  {{ storeForDrop(item)?.attributes.store_name }}
                </div>
                <div class="text-sm text-gray-900">
                  {{ fulfillmentDaysForDrop(item).join(', ') }}
                </div>
                <div class="text-sm text-gray-900 truncate">
                  {{ autochargeTimeForDrops(item) }}
                </div>
                <div class="text-sm text-gray-900">
                  <BellIcon
                    v-if="remindersForDrop(item).length"
                    v-tippy="{
                      content: `${remindersForDrop(item).length} ${
                        remindersForDrop(item).length > 1 ? 'reminders' : 'reminder'
                      } set`
                    }"
                    class="h-4 w-4 inline"
                  />
                  <Square2StackIcon
                    v-if="duplicatorForDrop(item)"
                    v-tippy="{
                      content: recurrenceForDrop(item)
                    }"
                    class="h-4 w-4 inline"
                  />
                </div>
                <div>
                  <!-- whitespace-nowrap: "Reminder failed" used to wrap to two
                       lines and stretch the row. -->
                  <span
                    :class="[
                      dropStatus(item).classes,
                      'px-2 inline-flex whitespace-nowrap text-xs leading-5 font-semibold rounded-full'
                    ]"
                  >
                    {{ dropStatus(item).label }}
                  </span>
                </div>
              </TablistGrid>
            </TablistRow>
          </NeueTablist>
        </div>
        <NeuePagination
          :size="activeList.length"
          :from="data.page * 8 + 1"
          :to="Math.min((data.page + 1) * 8, activeList.length)"
          @prev="
            ret => {
              data.page--
              ret()
            }
          "
          @next="
            ret => {
              data.page++
              ret()
            }
          "
        />
      </div>
      <!-- Drops exist, just none in the selected view. The filter stays above
           this so the merchant can switch back to one that has drops. -->
      <NeueWell v-else class="text-center">
        <EmptyState :plural="emptyViewLabel" :description="emptyViewDescription" />
      </NeueWell>
    </div>
    <NeueWell v-if="!data.list.length" class="text-center">
      <EmptyState plural="drops" />
    </NeueWell>
  </NeueLoading>
</template>
<script>
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { BellIcon, ChevronDownIcon, Square2StackIcon } from '@heroicons/vue/20/solid'
import { computed, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'

import NeueTablist from '@/components/neue/NeueTablist.vue'
import TablistHeader from '@/components/neue/TablistHeader.vue'
import TablistRow from '@/components/neue/TablistRow.vue'
import TablistGrid from '@/components/neue/TablistGrid.vue'
import NeuePagination from '@/components/neue/NeuePagination.vue'
import { serverDayToDateObject } from '@/helpers/dates'
import { dropStatus as dropStatusFor } from '@/helpers/dropStatus'
import { duplicatorRecurrenceDescription } from '@/helpers/duplicator'
import api from '@/store/api'
import { mapRelationship, mapRelationships } from '@/store/mappers'

export default {
  components: {
    BellIcon,
    ChevronDownIcon,
    Square2StackIcon,
    Popover,
    PopoverButton,
    PopoverPanel,
    NeueTablist,
    TablistHeader,
    TablistRow,
    TablistGrid,
    NeuePagination
  },
  props: {
    // Row click destination prefix; the drop id is appended.
    linkBase: { type: String, default: '/store/drops' }
  },
  setup() {
    const store = useStore()
    const loading = ref(true)
    const dropFilter = ref('upcoming')

    const dropColumns = [
      { width: '60px' },
      { width: 'minmax(220px, 2fr)' },
      { width: 'minmax(160px, 1fr)' },
      { width: 'minmax(120px, 1fr)' },
      { width: 'minmax(130px, 1fr)' },
      { width: '110px' },
      { width: '50px' },
      // Wide enough for the longest badge, "Reminder failed", on one line.
      { width: '130px' }
    ]

    const data = reactive({
      list: computed(() =>
        Object.values(store.state.objects.bottle_cycle || {})
          .sort((a, b) => a.id - b.id)
          .sort((a, b) => {
            const aDay = mapRelationships(a.relationships.fulfillment_availabilities.data).sort(
              (x, y) => new Date(x.attributes.date) - new Date(y.attributes.date)
            )[0]
            const bDay = mapRelationships(b.relationships.fulfillment_availabilities.data).sort(
              (x, y) => new Date(x.attributes.date) - new Date(y.attributes.date)
            )[0]
            return new Date(aDay?.attributes.date) - new Date(bDay?.attributes.date)
          })
      ),
      page: 0
    })

    const groupsForDrop = drop =>
      mapRelationships(drop.relationships.bottle_cycle_audience_pairs.data).map(x =>
        mapRelationship({ type: 'audience', id: x.attributes.audience_id })
      )

    const setDropFilter = value => {
      dropFilter.value = value
      data.page = 0
    }

    const dropFilterLabel = computed(() => {
      if (dropFilter.value === 'upcoming') return 'Upcoming'
      if (dropFilter.value === 'past') return 'Past'
      return 'No production days'
    })

    // Shown when the merchant has drops but none in the view they picked, so
    // the message names the view rather than claiming they have no drops.
    const emptyViewLabel = computed(() => {
      if (dropFilter.value === 'upcoming') return 'upcoming drops'
      if (dropFilter.value === 'past') return 'past drops'
      return 'drops without production days'
    })

    const emptyViewDescription = computed(
      () => `Change the ${dropFilterLabel.value} filter above to see your other drops.`
    )

    const dropStatus = drop => dropStatusFor(drop, remindersForDrop(drop))

    const storeForDrop = drop => drop.relationships.store.data && mapRelationship(drop.relationships.store.data)

    const duplicatorForDrop = drop =>
      drop.relationships.duplicator.data && mapRelationship(drop.relationships.duplicator.data)

    const recurrenceForDrop = drop => duplicatorRecurrenceDescription(duplicatorForDrop(drop)?.attributes)

    const autochargeTimeForDrops = drop => {
      if (!drop.relationships.bottle_autocharge_automation.data) return
      const autocharge = mapRelationship(drop.relationships.bottle_autocharge_automation.data)
      const automation = mapRelationship(autocharge.relationships.automation_scheduler.data)
      if (!automation) return
      return new Intl.DateTimeFormat([], {
        hour: 'numeric',
        minute: 'numeric'
      }).format(new Date(automation.attributes.datetime))
    }

    const remindersForDrop = drop => {
      if (!drop.relationships.bottle_reminder_automations.data) return []
      return mapRelationships(drop.relationships.bottle_reminder_automations.data)
    }

    const fulfillmentDaysForDrop = drop =>
      mapRelationships(drop.relationships.fulfillment_availabilities.data).map(x =>
        Intl.DateTimeFormat([], {
          day: 'numeric',
          month: 'short'
        }).format(serverDayToDateObject(x.attributes.date))
      )

    const getList = () => {
      const includes = [
        'audiences',
        'bottle_reminder_automations',
        'bottle_autofill_automation',
        'bottle_autocharge_automation',
        'fulfillment_availabilities',
        'duplicator',
        'store',
        'bottle_cycle_audience_pairs',
        'bottle_reminder_automations.automation_scheduler',
        'bottle_reminder_automations.message_template',
        'bottle_reminder_automations.message_template.attachments',
        'bottle_autofill_automation.automation_fields',
        'bottle_autocharge_automation.automation_scheduler'
      ]
      api.call('GET', `bottle_cycles?included=${includes.join(',')}`).then(() => {
        loading.value = false
      })
    }

    onMounted(() => {
      getList()
    })

    const upcomingDrops = computed(() =>
      data.list
        .filter(x =>
          mapRelationships(x.relationships.fulfillment_availabilities.data).some(
            y => serverDayToDateObject(y.attributes.date) > new Date()
          )
        )
        .sort((a, b) => {
          const aDay = mapRelationships(a.relationships.fulfillment_availabilities.data)
            .filter(x => serverDayToDateObject(x.attributes.date) > new Date())
            .sort((x, y) => new Date(x.attributes.date) - new Date(y.attributes.date))[0]
          const bDay = mapRelationships(b.relationships.fulfillment_availabilities.data)
            .filter(x => serverDayToDateObject(x.attributes.date) > new Date())
            .sort((x, y) => new Date(x.attributes.date) - new Date(y.attributes.date))[0]
          return new Date(aDay?.attributes.date) - new Date(bDay?.attributes.date)
        })
    )

    const pastDrops = computed(() =>
      data.list
        .filter(x =>
          mapRelationships(x.relationships.fulfillment_availabilities.data).some(
            y => serverDayToDateObject(y.attributes.date) < new Date()
          )
        )
        .sort((a, b) => {
          const aDay = mapRelationships(a.relationships.fulfillment_availabilities.data)
            .filter(x => serverDayToDateObject(x.attributes.date) < new Date())
            .sort((x, y) => new Date(y.attributes.date) - new Date(x.attributes.date))[0]
          const bDay = mapRelationships(b.relationships.fulfillment_availabilities.data)
            .filter(x => serverDayToDateObject(x.attributes.date) < new Date())
            .sort((x, y) => new Date(y.attributes.date) - new Date(x.attributes.date))[0]
          return new Date(bDay?.attributes.date) - new Date(aDay?.attributes.date)
        })
    )

    const noDaysDrops = computed(() =>
      data.list.filter(x => x.relationships.fulfillment_availabilities.data.length === 0).sort((a, b) => b.id - a.id)
    )

    const activeList = computed(() => {
      if (dropFilter.value === 'upcoming') return upcomingDrops.value
      if (dropFilter.value === 'past') return pastDrops.value
      return noDaysDrops.value
    })

    return {
      data,
      groupsForDrop,
      storeForDrop,
      autochargeTimeForDrops,
      remindersForDrop,
      fulfillmentDaysForDrop,
      duplicatorForDrop,
      recurrenceForDrop,
      setDropFilter,
      dropFilterLabel,
      emptyViewLabel,
      emptyViewDescription,
      activeList,
      dropStatus,
      loading,
      dropColumns
    }
  }
}
</script>
